import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useState } from 'react'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useLoadAllItemsQuery, useMutation } from '@sequencehq/api/utils'
import get from 'lodash/get'
import {
  RevenueRecognitionMethod,
  revenueRecognitionOptions
} from 'common/types/revenueRecognition.types'
import { useShouldShowXeroMappings } from 'modules/Products/hooks/useShouldShowXeroMappings'
import { useXeroLedgerAccountOptions } from 'modules/Products/hooks/useXeroLedgerAccountOptions'
import { buildXeroMappingsForUpdate } from 'Products/utils/buildXeroMappingsPayload'
import {
  Product as ProductEntity,
  ProductXeroLedgerAccountMapping
} from 'Products/types'

type ProductFormValues = {
  name: string
  label?: string
  taxCategoryId?: string
  revenueRecognitionMethod?: RevenueRecognitionMethod
  deferredRevenueXeroAccountMappingId?: string
  recognizedRevenueXeroAccountMappingId?: string
  unbilledRevenueXeroAccountMappingId?: string
}

const findMappingId = (
  mappings: ProductXeroLedgerAccountMapping[] | undefined,
  type: ProductXeroLedgerAccountMapping['mappableLedgerAccount']
): string | undefined =>
  mappings?.find(m => m.mappableLedgerAccount === type)
    ?.xeroLedgerAccountMappingId

export const useEditProduct = (productDetails: ProductEntity) => {
  const flags = useFeatureFlags()
  const showXeroMappings = useShouldShowXeroMappings()
  const {
    isLoading: isXeroMappingsLoading,
    hasMappings: hasXeroMappings,
    deferredRevenueOptions,
    recognizedRevenueOptions,
    unbilledRevenueOptions,
    initialDeferredRevenueMappingId,
    initialRecognizedRevenueMappingId,
    initialUnbilledRevenueMappingId
  } = useXeroLedgerAccountOptions()

  // Deferred/recognized selection is only required when the mapping dropdowns
  // are actually rendered — i.e. the feature is on AND the account has mappings
  // to choose from. With no mappings the banner shows instead, so requiring the
  // (hidden) fields would leave the form permanently invalid.
  const xeroMappingsAvailable = showXeroMappings && hasXeroMappings

  const { mutateAsync: putProduct } = useMutation(
    dashboardv99990101Client.putProduct
  )

  const { data: taxCategoriesData } = useLoadAllItemsQuery(
    dashboard20240730Client.getTaxCategories
  )

  const taxCategories =
    taxCategoriesData?.pages.flatMap(page => page.items) ?? []

  const initialValues = {
    label: productDetails.label,
    name: productDetails.name,
    taxCategoryId: productDetails.taxCategoryId,
    revenueRecognitionMethod: productDetails.revenueRecognitionMethod,
    deferredRevenueXeroAccountMappingId:
      findMappingId(
        productDetails.xeroLedgerAccountMappings,
        'DEFERRED_REVENUE'
      ) ?? initialDeferredRevenueMappingId,
    recognizedRevenueXeroAccountMappingId:
      findMappingId(
        productDetails.xeroLedgerAccountMappings,
        'RECOGNIZED_REVENUE'
      ) ?? initialRecognizedRevenueMappingId,
    unbilledRevenueXeroAccountMappingId:
      findMappingId(
        productDetails.xeroLedgerAccountMappings,
        'UNBILLED_REVENUE'
      ) ?? initialUnbilledRevenueMappingId
  }

  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const { fields, queries } = useForm<ProductFormValues>({
    value: initialValues,
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'label',
        validation: []
      },
      {
        property: 'taxCategoryId',
        disabled: () => taxCategories.length === 0,
        validation: [required]
      },
      {
        property: 'revenueRecognitionMethod',
        disabled: () => !flags.useRevRec,
        options: revenueRecognitionOptions
      },
      {
        property: 'deferredRevenueXeroAccountMappingId',
        disabled: () => !showXeroMappings || isXeroMappingsLoading,
        options: deferredRevenueOptions,
        validation: xeroMappingsAvailable ? [required] : []
      },
      {
        property: 'recognizedRevenueXeroAccountMappingId',
        disabled: () => !showXeroMappings || isXeroMappingsLoading,
        options: recognizedRevenueOptions,
        validation: xeroMappingsAvailable ? [required] : []
      },
      {
        // Unbilled revenue is an optional product-level override, unlike
        // deferred and recognized which are required when Xero mappings show.
        property: 'unbilledRevenueXeroAccountMappingId',
        disabled: () => !showXeroMappings || isXeroMappingsLoading,
        options: unbilledRevenueOptions,
        validation: []
      }
    ],
    onChange: () => {
      setShowValidationErrors(false)
    }
  })

  const changeset:
    | Record<
        keyof ProductFormValues,
        {
          oldValue: unknown
          newValue: unknown
        }
      >
    | Record<string, never> = Object.keys(queries.formData).reduce(
    (acc, fieldKey) => {
      const newValue = get(queries.formData, fieldKey)
      const oldValue = get(initialValues, fieldKey)
      if (oldValue !== newValue) {
        return {
          ...acc,
          [fieldKey]: {
            newValue,
            oldValue
          }
        }
      }

      return acc
    },
    {}
  )

  // Returns undefined when the form is invalid (nothing submitted), otherwise
  // the mutation promise. mutateAsync rejects on API error, so the caller drives
  // the submitting state and success/failure toasts.
  const onEditProduct = () => {
    if (!queries.isValid) {
      setShowValidationErrors(true)
      return
    }

    return putProduct({
      id: productDetails.id,
      body: {
        name: queries.formData.name,
        label: queries.formData.label,
        taxCategoryId: queries.formData.taxCategoryId,
        // revenueRecognitionMethod is only a product concept when the flag is on
        ...(flags.useRevRec
          ? {
              revenueRecognitionMethod:
                queries.formData.revenueRecognitionMethod
            }
          : {}),
        ...(xeroMappingsAvailable
          ? {
              xeroLedgerAccountMappings: buildXeroMappingsForUpdate({
                deferredId:
                  queries.formData.deferredRevenueXeroAccountMappingId,
                recognizedId:
                  queries.formData.recognizedRevenueXeroAccountMappingId,
                unbilledId: queries.formData.unbilledRevenueXeroAccountMappingId
              })
            }
          : {})
      }
    })
  }

  return {
    changeset,
    showXeroMappings,
    isXeroMappingsLoading,
    hasXeroMappings,
    fieldsConfig: {
      ...fields,
      taxCategoryId: {
        ...fields.taxCategoryId,
        hidden: false
      }
    },
    functions: {
      editProduct: onEditProduct
    }
  }
}
